import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Stack,
  Paper,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Description as DocumentIcon,
} from '@mui/icons-material';

const steps = ['Select Document', 'Configure Model', 'Review & Generate'];

function PolicyModelCreator({ onSuccess, onCancel, preselectedDocument = null, preselectedCollection = null }) {
  const [activeStep, setActiveStep] = useState(0);
  const [collections, setCollections] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(preselectedCollection || 'documents');
  const [selectedDocument, setSelectedDocument] = useState(preselectedDocument || '');
  const [modelName, setModelName] = useState('');
  const [description, setDescription] = useState('');
  const [llm, setLlm] = useState('ollama');
  const [model, setModel] = useState('llama3');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generatedModel, setGeneratedModel] = useState(null);

  useEffect(() => {
    loadCollections();
  }, []);

  useEffect(() => {
    if (selectedCollection) {
      loadDocuments(selectedCollection);
    }
  }, [selectedCollection]);

  const loadCollections = async () => {
    try {
      const response = await fetch('/api/v1/rag/collections');
      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data.collections)) {
          setCollections(data.collections);
        } else if (data.collections && typeof data.collections === 'object') {
          setCollections(Object.values(data.collections));
        }
      }
    } catch (err) {
      console.error('Failed to load collections:', err);
    }
  };

  const loadDocuments = async (collectionName) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/v1/rag/collections/${collectionName}/documents`);
      if (response.ok) {
        const data = await response.json();
        setDocuments(data.documents || []);
      }
    } catch (err) {
      console.error('Failed to load documents:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (activeStep === 0 && !selectedDocument) {
      setError('Please select a document');
      return;
    }
    if (activeStep === 1 && !modelName.trim()) {
      setError('Please enter a model name');
      return;
    }
    setError(null);
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setError(null);
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);

    try {
      const response = await fetch('/api/v1/policy/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          document_id: selectedDocument,
          collection: selectedCollection,
          model_name: modelName,
          description: description,
          llm: llm,
          model: model || undefined,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setGeneratedModel(data.model);
        if (onSuccess) {
          setTimeout(() => {
            onSuccess();
          }, 2000);
        }
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to generate model');
      }
    } catch (err) {
      setError(`Error generating model: ${err.message}`);
    } finally {
      setGenerating(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Select a document to generate policy model from
            </Typography>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Collection</InputLabel>
              <Select
                value={selectedCollection}
                label="Collection"
                onChange={(e) => {
                  setSelectedCollection(e.target.value);
                  setSelectedDocument('');
                }}
              >
                {collections.map((collection) => (
                  <MenuItem key={collection.name} value={collection.name}>
                    {collection.name} ({collection.count} docs)
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : documents.length === 0 ? (
              <Alert severity="info">
                No documents found in this collection. Upload a document first.
              </Alert>
            ) : (
              <FormControl fullWidth>
                <InputLabel>Document</InputLabel>
                <Select
                  value={selectedDocument}
                  label="Document"
                  onChange={(e) => setSelectedDocument(e.target.value)}
                >
                  {documents.map((doc) => (
                    <MenuItem key={doc.filename} value={doc.filename}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <DocumentIcon sx={{ mr: 1 }} />
                        {doc.filename} ({doc.chunks} chunks)
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Configure your policy model
            </Typography>

            <TextField
              fullWidth
              label="Model Name"
              value={modelName}
              onChange={(e) => setModelName(e.target.value)}
              required
              sx={{ mb: 3 }}
              placeholder="e.g., City Permits 2024"
            />

            <TextField
              fullWidth
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              rows={3}
              sx={{ mb: 3 }}
              placeholder="Describe what this policy model is for..."
            />

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>LLM Provider</InputLabel>
              <Select value={llm} label="LLM Provider" onChange={(e) => setLlm(e.target.value)}>
                <MenuItem value="ollama">Ollama</MenuItem>
                <MenuItem value="openai">OpenAI</MenuItem>
                <MenuItem value="llama-api">Llama API</MenuItem>
                <MenuItem value="deepseek">DeepSeek</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Model (optional)"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              placeholder="e.g., llama3, gpt-3.5-turbo"
              helperText="Leave empty to use provider default"
            />
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Review and Generate
            </Typography>

            {generatedModel ? (
              <Alert
                icon={<CheckIcon />}
                severity="success"
                sx={{ mb: 3 }}
              >
                Model generated successfully!
              </Alert>
            ) : (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Model Configuration
                  </Typography>
                  <Stack spacing={1} sx={{ mt: 2 }}>
                    <Box>
                      <strong>Name:</strong> {modelName}
                    </Box>
                    <Box>
                      <strong>Description:</strong> {description || 'No description'}
                    </Box>
                    <Box>
                      <strong>Source Document:</strong>{' '}
                      <Chip label={selectedDocument} size="small" />
                    </Box>
                    <Box>
                      <strong>Collection:</strong> {selectedCollection}
                    </Box>
                    <Box>
                      <strong>LLM:</strong> {llm} {model && `(${model})`}
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            )}

            {generating && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress sx={{ mb: 2 }} />
                <Typography>Generating policy model...</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  This may take a few moments
                </Typography>
              </Box>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box>
      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {renderStepContent()}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button onClick={activeStep === 0 ? onCancel : handleBack}>
          {activeStep === 0 ? 'Cancel' : 'Back'}
        </Button>
        <Button
          variant="contained"
          onClick={activeStep === steps.length - 1 ? handleGenerate : handleNext}
          disabled={generating || (activeStep === steps.length - 1 && generatedModel)}
        >
          {activeStep === steps.length - 1
            ? generating
              ? 'Generating...'
              : 'Generate Model'
            : 'Next'}
        </Button>
      </Box>
    </Box>
  );
}

export default PolicyModelCreator;

